import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as helpResourcesActions from './help-resources.actions';
import { of } from 'rxjs';
import { HelpResourcesService, PassportHelpService } from '@emi/shared/data-access';
import { Office, TechSupport, AccountManager, Definition, DefinitionCategory } from '@emi/shared/models';
import { HttpErrorResponse } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { HelpResourcesState } from './help-resources.reducer';
import { getHelpResourcesState } from './help-resources.selectors';

@Injectable({ providedIn: 'root' })
export class HelpResourcesEffects {
    private helpResourcesState: HelpResourcesState;

    constructor(
        private actions$: Actions,
        private passportHelpService: PassportHelpService,
        private helpResourcesService: HelpResourcesService,
        private helpResourcesStore: Store<HelpResourcesState>
    ) {
        this.helpResourcesStore.pipe(select(getHelpResourcesState)).subscribe((state) => (this.helpResourcesState = state));
    }

    private fetchContactUsOffices$ = createEffect(() =>
        this.actions$.pipe(
            ofType(helpResourcesActions.fetchContactUsOffices),
            switchMap(() => {
                if (this.helpResourcesState.offices && this.helpResourcesState.offices.length > 0) {
                    return of(helpResourcesActions.getContactUsOfficeSuccess({ offices: this.helpResourcesState.offices }));
                }

                return this.passportHelpService.getOffices().pipe(
                    map((offices: Office[]) => {
                        return helpResourcesActions.getContactUsOfficeSuccess({ offices });
                    }),

                    catchError((error: HttpErrorResponse) => {
                        return of(helpResourcesActions.contactUsDetailsFailure({ error }));
                    })
                );
            })
        )
    );

    private fetchContactUsTechSupport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(helpResourcesActions.fetchContactUsTechSupport),
            switchMap(() => {
                if (this.helpResourcesState.techSupport) {
                    return of(helpResourcesActions.getContactUsTechSupportSuccess({ techSupport: this.helpResourcesState.techSupport }));
                }

                return this.passportHelpService.getTechSupport().pipe(
                    map((techSupport: TechSupport) => {
                        return helpResourcesActions.getContactUsTechSupportSuccess({ techSupport });
                    }),

                    catchError((error: HttpErrorResponse) => {
                        return of(helpResourcesActions.contactUsDetailsFailure({ error }));
                    })
                );
            })
        )
    );

    private sendFeedback$ = createEffect(() =>
        this.actions$.pipe(
            ofType(helpResourcesActions.sendContactUsFeedback),
            switchMap(({ email }) => {
                return this.passportHelpService.sendFeedback(email).pipe(
                    map(() => {
                        return helpResourcesActions.sendContactUsFeedbackSuccess();
                    })
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(helpResourcesActions.contactUsDetailsFailure({ error }));
            })
        )
    );

    private fetchAccountManagers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(helpResourcesActions.fetchContactUsAccountManagers),
            switchMap(() => {
                if (this.helpResourcesState.accountManagers && this.helpResourcesState.accountManagers.length > 0) {
                    return of(
                        helpResourcesActions.getContactUsAccountManagerSuccess({ accountManagers: this.helpResourcesState.accountManagers })
                    );
                }

                return this.passportHelpService.getAccountManagers().pipe(
                    map((accountManagers: AccountManager[]) => helpResourcesActions.getContactUsAccountManagerSuccess({ accountManagers })),

                    catchError((error: HttpErrorResponse) => of(helpResourcesActions.contactUsDetailsFailure({ error })))
                );
            })
        )
    );

    private fetchCategoryDefinitions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(helpResourcesActions.fetchCategoryDefinitions),
            switchMap(({ categoryId, categoryTitle }) => {
                return this.helpResourcesService.findCategoryDefinitions(categoryId, categoryTitle).pipe(
                    map((categoryDefinitions: DefinitionCategory) => {
                        return helpResourcesActions.getCategoryDefinitionsSuccess({ categoryDefinitions });
                    }),

                    catchError((error: HttpErrorResponse) => {
                        return of(helpResourcesActions.getCategoryDefinitionsFailure({ error }));
                    })
                );
            })
        )
    );

    private fetchDefinitions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(helpResourcesActions.getDefinitions),
            switchMap(() => {
                return this.helpResourcesService.getDefinitions().pipe(
                    map((definitions) => {
                        return helpResourcesActions.getDefinitionsSuccess({ definitions });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(helpResourcesActions.getDefinitionsFailure({ error }));
                    })
                );
            })
        )
    );

    private fetchFaqCategories$ = createEffect(() =>
        this.actions$.pipe(
            ofType(helpResourcesActions.getFaqCategories),
            switchMap(() => {
                return this.helpResourcesService.getFaqCategories().pipe(
                    map((faqCategories) => {
                        return helpResourcesActions.getFaqCategoriesSuccess({ faqCategories: faqCategories });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(helpResourcesActions.getFaqCategoriesFailure({ error }));
                    })
                );
            })
        )
    );

    private fetchFaqCategoriesFaqs$ = createEffect(() =>
        this.actions$.pipe(
            ofType(helpResourcesActions.getFaqCategories),
            switchMap(() => {
                return this.helpResourcesService.getFaqs().pipe(
                    map((faqs) => {
                        return helpResourcesActions.getFaqCategoriesFaqsSuccess({ faqs: faqs });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(helpResourcesActions.getFaqCategoriesFaqsFailure({ error }));
                    })
                );
            })
        )
    );
}
